import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Typography, Skeleton, Button, Modal } from "@mui/material";

import { Media } from "../../../../../../types/types";
import { formatMediaUrl } from "../../../../../../utils/formatters/formatMedia";
import {
  ProgramVideoWatchButtonDisabledIcon,
  ProgramVideoWatchButtonIcon,
} from "../../../../assets/icons/service-details-icons";

const VideoItem = ({
  video: { media, title, isFree, duration },
  canRequest,
}: {
  canRequest?: boolean;
  video: { media: Media; title: string; isFree: boolean; duration: number };
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });

  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  const hasAccess = canRequest !== undefined ? canRequest : true;
  const canWatchVideo = isFree || !hasAccess;
  const isWatchButtonDisabled = !canWatchVideo;

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const handleOpenVideoModal = () => setIsVideoModalOpen(true);
  const handleCloseVideoModal = () => setIsVideoModalOpen(false);

  const videoData = { media, title, isFree, duration };

  return (
    <>
      <Box sx={styles.videoItem}>
        <Box sx={styles.videoThumbnailContainer}>
          <Box component="img" src={formatMediaUrl(media.thumbnail)} sx={styles.videoThumbnail} />
          {isFree && <Chip label={t("free")} sx={styles.videoFreeChip} />}
        </Box>
        <Box sx={styles.videoItemInfo}>
          <Box sx={styles.videoInfo}>
            <Typography sx={styles.videoTitle}>{title}</Typography>
            <Typography sx={styles.videoDuration}>{t("duration", { hours: hours, minutes: minutes })}</Typography>
          </Box>
          <Button
            disabled={isWatchButtonDisabled}
            sx={{
              ...styles.videoWatchButton,
              color: isWatchButtonDisabled ? "var(--Dark---600, #707070) !important" : "#008B07",
            }}
            variant="text-sm"
            startIcon={
              isWatchButtonDisabled ? <ProgramVideoWatchButtonDisabledIcon /> : <ProgramVideoWatchButtonIcon />
            }
            onClick={handleOpenVideoModal}
          >
            {t("watch")}
          </Button>
        </Box>
      </Box>
      <VideoModal video={videoData} open={isVideoModalOpen} onClose={handleCloseVideoModal} />
    </>
  );
};

const VideoItemLoader = () => {
  return (
    <Box sx={styles.loadingItem}>
      <Skeleton variant="rectangular" sx={styles.loadingThumbnail} animation="wave" />
      <Box sx={styles.loadingContent}>
        <Box sx={styles.videoInfo}>
          <Skeleton variant="rounded" sx={styles.loadingTitle} animation="wave" />
          <Skeleton variant="rectangular" sx={styles.loadingDuration} animation="wave" />
        </Box>
        <Skeleton variant="rounded" sx={styles.loadingButton} animation="wave" />
      </Box>
    </Box>
  );
};

const VideoModal = ({
  video,
  open,
  onClose,
}: {
  video: { media: Media; title: string; isFree: boolean; duration: number };
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.videoModal}>
        <Box component="video" controls src={formatMediaUrl(video.media.url)} sx={styles.videoModalThumbnail} />
      </Box>
    </Modal>
  );
};

export { VideoItem, VideoItemLoader };

const styles = {
  videoItem: {
    display: "flex",
    gap: "16px",
  },

  videoThumbnailContainer: {
    width: "89px",
    height: "74px",
    borderRadius: "6px",
    border: "0px solid #BCC1CA",
    overflow: "hidden",
    position: "relative",
  },

  videoThumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  videoItemInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
  },

  videoInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  videoTitle: {
    fontFamily: "Montserrat",
    color: "#272E38",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "16px",
  },

  videoDuration: {
    fontFamily: "Montserrat",
    color: "var(--Dark---800, #414141)",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },

  videoFreeChip: {
    height: "auto",
    fontFamily: "Montserrat",
    background: "#FFF",
    color: "var(--Green---100, #008B07)",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 700,
    padding: "2px 8px",
    position: "absolute",
    top: "5px",
    left: "2px",
    "& .MuiChip-label": {
      padding: "0",
    },
  },

  videoWatchButton: {
    width: "fit-content",
    padding: "0",
    background: "transparent !important",
  },

  videoModalThumbnail: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  videoModal: {
    width: "1024px",
    height: "700px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#FFF",
    borderRadius: "12px ",
    overflow: "auto",
    display: "flex",
  },

  loadingItem: {
    display: "flex",
    gap: "16px",
    width: "100%",
  },

  loadingThumbnail: {
    width: "89px",
    height: "74px",
    borderRadius: "6px",
  },

  loadingContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    height: "74px",
  },

  loadingTitle: {
    width: "70%",
    height: "16px",
  },

  loadingDuration: {
    width: "30%",
    height: "10px",
  },

  loadingButton: {
    width: "60px",
    height: "24px",
  },
} as const;
